import React, { Component, Fragment } from 'react';
import {
  GE_WEB_MAP_GE_ORDER_STATE,
  ONGOING_ORDER_STATES,
  ORDER_TYPES,
  ORDER_TYPES_SOURCE_ENUMS,
} from 'constants/order-constants';
import I18n from 'i18n-js';
import Select from 'components/v2/select/select';
import { ExceptMobile } from 'components/v2/responsive';
import { Button, Tooltip } from 'grab-ui';
import { getCompanyID, getUserCountryCode } from 'helper/user';
import { cat, custom, event, trackClick } from 'common/utils/instrumentation';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { isOnGoingOrderCancellable } from 'helper/history-order/history-order';
import TableActionColumn from 'components/v2/table-action-column/table-action-column';
import printIcon from 'public/icons/print.svg';
import trackIcon from 'public/icons/trackBooking.svg';
import cancelIcon from 'public/icons/cancel.svg';
import {
  APP_BASE_URL_PATH,
  WAYBILLS_ORDERS_KEY,
  WAYBILLS_PAGE_READY,
} from 'constants/app-constants';
import uuidv1 from 'uuid/v1';
import { ROUTES_V2_PATHS } from 'constants/route-constants';
import { enableWaybill } from 'helper/configuration';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_BOOKING_EVENTS,
  SCRIBE_EVENT_PARAMS,
} from 'applications/scribe/instrumentations/track';
import shopifyLogo from 'public/images/shopify-logo.png';
import {
  extractOrderIDs,
  MAX_ORDERS_GUEST_ORDER_TRACKING,
  redirectGuestOrderTracking,
} from 'helper/guest-order-tracking';
import { pushGADriverAssignedEvent } from 'helper/ga-helper';

import OngoingBookingsTable from './ongoing-bookings-table';
import css from './ongoing-bookings-section.less';

const getActionDropdownID = order => `ant-dropdown-${order.deliveryID}`;

class OngoingBookingsSection extends Component {
  constructor(props) {
    super(props);
    this.paginationOptions = [
      {
        value: '5',
        title: I18n.t('v2.bookingsPage.paginationOption', { num: 5 }),
        disabled: false,
        child: I18n.t('v2.bookingsPage.paginationOption', { num: 5 }),
      },
      {
        value: '20',
        title: I18n.t('v2.bookingsPage.paginationOption', { num: 20 }),
        disabled: false,
        child: I18n.t('v2.bookingsPage.paginationOption', { num: 20 }),
      },
      {
        value: '50',
        title: I18n.t('v2.bookingsPage.paginationOption', { num: 50 }),
        disabled: false,
        child: I18n.t('v2.bookingsPage.paginationOption', { num: 50 }),
      },
    ];
    this.state = {
      selectedPageSize: this.paginationOptions[0].value,
      existingOrderTypes: [],
    };
    this.waybillsPageNameToOrdersMapping = {};
  }

  componentDidMount() {
    window.addEventListener('message', this.waybillsPageReadyListener);
    this.updateExistingOrderTypes(
      this.computeExistingOrderTypes(_get(this.props, 'ongoingOrders', [])),
    );
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.waybillsPageReadyListener);
  }

  getHelpCenterLink = () => {
    const { userInfo } = this.props;
    const countryCode = getUserCountryCode(userInfo);
    return I18n.t(
      `v2.bookingsPage.cancelBookingHelpLink.${countryCode.toLowerCase()}`,
    );
  };

  ongoingOrdersRowActions = order => {
    const { userInfo, onClickTrackOrder, onClickCancelOrder } = this.props;
    const companyID = getCompanyID(userInfo);
    const { trackingURL, webTrackingURLsMap } = order;
    const disabled = _isEmpty(trackingURL) && _isEmpty(webTrackingURLsMap);
    const isNotOnGoingOrderCancellable = !isOnGoingOrderCancellable(order);

    const dropdownItems = [
      {
        value: I18n.t('v2.bookingsPage.trackDelivery'),
        icon: <img src={trackIcon} alt="" />,
        onClick: onClickTrackOrder(order),
        disabled,
      },
      {
        value: I18n.t('v2.bookingsPage.cancelBooking'),
        icon: <img src={cancelIcon} alt="" />,
        onClick: onClickCancelOrder(order),
        disabled: isNotOnGoingOrderCancellable,
        tooltip: isNotOnGoingOrderCancellable
          ? {
              placement: 'bottomRight',
              text: (
                <p>
                  {I18n.t('v2.bookingsPage.tooltip.cancelBookingTip')}
                  <a href={this.getHelpCenterLink()} target="_blank">
                    {' '}
                    {I18n.t('appSider.contactUs')}
                  </a>
                </p>
              ),
            }
          : null,
      },
    ];
    if (enableWaybill()) {
      dropdownItems.unshift({
        value: I18n.t('v2.bookingsPage.printLabel'),
        onClick: this.printLabel.bind(this, order),
        icon: <img src={printIcon} alt="" />,
      });
    }

    return (
      <TableActionColumn
        id={getActionDropdownID(order)}
        items={dropdownItems}
        buttonClick={() => {
          trackClick(
            event.BOOKINGS_CLICKS,
            cat.GE_BOOKINGS,
            'ONGOING_BOOKINGS_DETAILS',
            {
              [custom.COMPANY_ID]: companyID,
              ORDER_ID: order.deliveryID,
            },
          );

          scribeTrackEvent({
            stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
            eventName: SCRIBE_BOOKING_EVENTS.ONGOING_BOOKINGS_DETAILS,
            facets: {
              ...getCommonFacet(userInfo),
              [SCRIBE_EVENT_PARAMS.ORDER_ID]: order.deliveryID,
              [SCRIBE_EVENT_PARAMS.BOOKING_CODE]: order.bookingCode,
            },
          });
        }}
        placement="bottomRight"
      />
    );
  };

  onSelectPagination = value => {
    const { scribeExpressBookingsWebShowOngoingBookings } = this.props;
    scribeExpressBookingsWebShowOngoingBookings(value);
    this.setState({ selectedPageSize: value });
  };

  getOngoingOrderStateToRender = () =>
    ONGOING_ORDER_STATES.map(state => ({
      value: state.state,
      title: I18n.t(state.text),
      child: I18n.t(state.text),
    }));

  getOngoingOrdersToRender = () => {
    const { selectedOngoingState, selectedOngoingOrderType } = this.props;
    const { ongoingOrders } = this.props;

    return ongoingOrders.filter(order => {
      const sourceSelected = ORDER_TYPES[selectedOngoingOrderType];
      const orderSource = _get(order, 'source', 0);

      let sourceMatched = true;
      if (sourceSelected !== ORDER_TYPES.ALL) {
        sourceMatched =
          orderSource === ORDER_TYPES_SOURCE_ENUMS[sourceSelected];
      }

      const gewebStateMap = GE_WEB_MAP_GE_ORDER_STATE[selectedOngoingState];
      const stateMatched = gewebStateMap
        ? gewebStateMap.includes(order.status)
        : order.status === selectedOngoingState;

      return sourceMatched && stateMatched;
    });
  };

  waybillsPageReadyListener = evt => {
    if (evt.origin !== window.location.origin) return;
    const readyEvent = _get(evt, `data.${WAYBILLS_PAGE_READY}`);
    const windowName = _get(evt, 'source.name');
    if (!readyEvent || !windowName) return;

    const ordersForPage = _get(
      this.waybillsPageNameToOrdersMapping,
      windowName,
    );
    if (!ordersForPage) return;
    evt.source.postMessage({ [WAYBILLS_ORDERS_KEY]: ordersForPage });
  };

  printLabels = () => {
    const { selectedOrders, onClickBulkPrintLabel } = this.props;
    if (_isEmpty(selectedOrders)) return;
    onClickBulkPrintLabel(selectedOrders.length);
    this.openWaybillsPage(selectedOrders);
  };

  printLabel = order => {
    if (!order) return;

    const { onClickPrintLabel } = this.props;
    onClickPrintLabel();

    this.openWaybillsPage([order]);
  };

  cancelMultipleOrders = () => {
    const { selectedOrders } = this.props;
    const { onClickCancelMultipleOrders } = this.props;
    onClickCancelMultipleOrders(selectedOrders);
  };

  openWaybillsPage = orders => {
    const newWindowName = uuidv1();
    this.waybillsPageNameToOrdersMapping[newWindowName] = orders;
    window.open(
      `${window.location.origin}${APP_BASE_URL_PATH}${
        ROUTES_V2_PATHS.waybills
      }`,
      newWindowName,
    );
  };

  computeExistingOrderTypes = orders =>
    Object.keys(ORDER_TYPES_SOURCE_ENUMS).filter(
      ot =>
        !!orders.find(
          o => _get(o, 'source', 0) === ORDER_TYPES_SOURCE_ENUMS[ot],
        ),
    );

  updateExistingOrderTypes = newExistingOrderTypes => {
    this.setState({
      existingOrderTypes: newExistingOrderTypes,
    });
  };

  getOngoingOrderTypesToSelect = () => {
    const { existingOrderTypes } = this.state;
    let options = [
      {
        value: ORDER_TYPES.ALL,
        title: I18n.t('history.orderTypes.all'),
      },
    ];
    options = options.concat(
      Object.keys(ORDER_TYPES)
        .filter(ot => existingOrderTypes.find(o => o === ot))
        .map(ot => ({
          value: ot,
          title: I18n.t(`history.orderTypes.${ot.toLowerCase()}`),
        })),
    );
    return options.map(opt => ({
      value: opt.value,
      title: opt.title,
      child: opt.title,
      icon: opt.value === ORDER_TYPES.SHOPIFY ? shopifyLogo : undefined,
    }));
  };

  isDisableCancelBookingsButton = selectedOrders => {
    if (!_isEmpty(selectedOrders)) {
      return selectedOrders.some(order => !isOnGoingOrderCancellable(order));
    }
    return true;
  };

  processGAEventForDriverAssigned = (currentOrders, prevOrders) => {
    const prevOrderMap = new Map(
      prevOrders.map(prevOrder => [prevOrder.deliveryID, prevOrder]),
    );
    const driverAssigned = currentOrders.map(order => {
      const prevOrder = prevOrderMap.get(order.deliveryID);
      if (prevOrder) {
        if (order.driver !== prevOrder.driver) {
          return order;
        }
      }
    });

    const { userInfo } = this.props;

    driverAssigned.map(order => {
      if (order) {
        pushGADriverAssignedEvent(
          userInfo,
          _get(order, 'amount', 0),
          _get(order, 'service.serviceName'),
          _get(order, 'currency.code'),
        );
      }
    });
  };

  componentDidUpdate(prevProps) {
    if (_get(prevProps, 'ongoingOrders', []).length > 0) {
      this.processGAEventForDriverAssigned(
        this.props.ongoingOrders,
        prevProps.ongoingOrders,
      );
    }
    const newExistingOrderTypes = this.computeExistingOrderTypes(
      _get(this.props, 'ongoingOrders', []),
    );
    if (!_isEqual(newExistingOrderTypes, this.state.existingOrderTypes)) {
      this.updateExistingOrderTypes(newExistingOrderTypes);
    }
  }

  render() {
    const {
      ongoingOrders,
      selectedOrders,
      ongoingOrdersLastUpdate,
      isOngoingOrdersLoading,
      selectedOngoingState,
      onSelectOngoingState,
      selectedOngoingOrderType,
      onSelectOngoingOrderType,
      onSelectOrders,
      enableWebCancellation,
    } = this.props;
    const { selectedPageSize } = this.state;
    const ongoingOrderQty = ongoingOrders.length;
    const ongoingOrderStates = this.getOngoingOrderStateToRender();
    const ongoingOrdersToRender = this.getOngoingOrdersToRender();
    const ongoingOrderTypesToSelect = this.getOngoingOrderTypesToSelect();
    const disableCancelBookingButton = this.isDisableCancelBookingsButton(
      selectedOrders,
    );
    const selectedOrderIDs = extractOrderIDs(selectedOrders);

    const renderCancelOrderButton = (
      <Button
        className={css.cancelButton}
        onClick={this.cancelMultipleOrders}
        disabled={disableCancelBookingButton}
      >
        <img src={cancelIcon} alt="" />
        {I18n.t('v2.bookingsPage.cancelBookings')}
      </Button>
    );

    return (
      <Fragment>
        <div className={css.headerSection}>
          <div>
            <h5>
              {I18n.t('v2.homepage.orderOverview.ongoingBooking.title', {
                amt: ongoingOrderQty || 0,
              })}
            </h5>
            <ExceptMobile>
              <h6>
                {I18n.t('v2.bookingsPage.lastUpdatedAt', {
                  time: ongoingOrdersLastUpdate,
                })}
              </h6>
            </ExceptMobile>
          </div>
          <div className={css.filterContainer}>
            <div className={css.selects}>
              {ongoingOrderTypesToSelect.length > 2 && (
                <Select
                  placeholder=""
                  options={ongoingOrderTypesToSelect}
                  className={css.orderStatusSelect}
                  onSelect={onSelectOngoingOrderType}
                  value={selectedOngoingOrderType}
                />
              )}
              <Select
                placeholder=""
                options={ongoingOrderStates}
                className={css.orderStatusSelect}
                onSelect={onSelectOngoingState}
                value={selectedOngoingState}
              />
              <Select
                className={css.paginationSelect}
                options={this.paginationOptions}
                value={selectedPageSize}
                onSelect={this.onSelectPagination}
              />
            </div>
            {selectedOrders.length > 0 && (
              <div className={css.actions}>
                <span className={css.selectedOrderCount}>
                  {I18n.t(
                    selectedOrders.length > 1
                      ? 'v2.bookingsPage.selectedBookingsPlural'
                      : 'v2.bookingsPage.selectedBookingSingle',
                    {
                      num: selectedOrders.length,
                    },
                  )}
                </span>
                <div>
                  <Button
                    className={css.printButton}
                    onClick={this.printLabels}
                  >
                    <img src={printIcon} alt="" />
                    {I18n.t('v2.bookingsPage.printLabels')}
                  </Button>
                  <Button
                    className={css.trackButton}
                    disabled={
                      _isEmpty(selectedOrderIDs) ||
                      selectedOrderIDs.length > MAX_ORDERS_GUEST_ORDER_TRACKING
                    }
                    onClick={() => redirectGuestOrderTracking(selectedOrderIDs)}
                  >
                    <img src={trackIcon} alt="" />
                    {I18n.t('v2.bookingsPage.trackDelivery')}
                  </Button>
                  {enableWebCancellation ? (
                    disableCancelBookingButton ? (
                      <Tooltip
                        placement={'bottomRight'}
                        title={
                          <p>
                            {I18n.t('v2.bookingsPage.tooltip.cancelBookingTip')}
                            <a href={this.getHelpCenterLink()} target="_blank">
                              {' '}
                              {I18n.t('appSider.contactUs')}
                            </a>
                          </p>
                        }
                      >
                        {renderCancelOrderButton}
                      </Tooltip>
                    ) : (
                      renderCancelOrderButton
                    )
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
        <OngoingBookingsTable
          ongoingOrdersRowActions={this.ongoingOrdersRowActions}
          orders={ongoingOrdersToRender}
          pagination={{ pageSize: Number(selectedPageSize) }}
          className={css.ongoingOrderTable}
          loading={isOngoingOrdersLoading}
          onSelectOrders={onSelectOrders}
        />
      </Fragment>
    );
  }
}

OngoingBookingsSection.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  ongoingOrders: PropTypes.arrayOf(PropTypes.shape({})),
  selectedOrders: PropTypes.arrayOf(PropTypes.shape({})),
  isOngoingOrdersLoading: PropTypes.bool.isRequired,
  ongoingOrdersLastUpdate: PropTypes.string,
  onClickTrackOrder: PropTypes.func,
  onClickCancelOrder: PropTypes.func,
  onClickCancelMultipleOrders: PropTypes.func,
  onSelectOngoingState: PropTypes.func.isRequired,
  selectedOngoingState: PropTypes.string,
  selectedOngoingOrderType: PropTypes.string,
  onSelectOngoingOrderType: PropTypes.func.isRequired,
  onClickPrintLabel: PropTypes.func.isRequired,
  onClickBulkPrintLabel: PropTypes.func.isRequired,
  scribeExpressBookingsWebShowOngoingBookings: PropTypes.func.isRequired,
  onSelectOrders: PropTypes.func.isRequired,
  enableWebCancellation: PropTypes.bool.isRequired,
};

OngoingBookingsSection.defaultProps = {
  ongoingOrders: [],
  selectedOrders: [],
  ongoingOrdersLastUpdate: '',
  onClickTrackOrder: () => {},
  onClickCancelOrder: () => {},
  onClickCancelMultipleOrders: () => {},
  selectedOngoingState: '',
  selectedOngoingOrderType: '',
};

export default OngoingBookingsSection;
